import React, { useCallback, useEffect, useRef, useState } from "react";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { Tabs } from "../../../../ui/components/Tabs";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { FeatherInfo, FeatherLoader } from "@subframe/core";
import {
  generateReport,
  GenerateReportParams,
} from "../../data/api/trialBalanceApi";
import { InlineExcelViewer } from "../components/InlineExcelViewer";
import { useAuth } from "../../../auth";

type TabType =
  | "profit-loss"
  | "balance-sheet"
  | "cash-flow"
  | "ar-aging"
  | "ap-aging";

// Helper function to get current month in YYYY-MM format
const getCurrentMonth = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

// Helper function to get 3-month window start in YYYY-MM format (inclusive range)
const getThreeMonthsAgo = (): string => {
  const now = new Date();
  // Inclusive 3-month range: (current month - 2) ... current month
  const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  const year = threeMonthsAgo.getFullYear();
  const month = String(threeMonthsAgo.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

// Helper function to convert month (YYYY-MM) to first day of month (YYYY-MM-DD)
const getFirstDayOfMonth = (monthStr: string): string => {
  return `${monthStr}-01`;
};

// Helper function to convert month (YYYY-MM) to last day of month (YYYY-MM-DD)
const getLastDayOfMonth = (monthStr: string): string => {
  const [year, month] = monthStr.split("-").map(Number);
  // Get last day of month: day 0 of next month
  const lastDay = new Date(year, month, 0).getDate();
  return `${monthStr}-${String(lastDay).padStart(2, "0")}`;
};

export const ReportingPage: React.FC = () => {
  return (
    <DefaultPageLayout>
      <ReportingPageContent autoApplyOnMount />
    </DefaultPageLayout>
  );
};

type ReportingPageContentProps = {
  /** When true, runs the same logic as clicking Apply once after mount (e.g. Client Books). */
  autoApplyOnMount?: boolean;
};

// Re-usable content (no surrounding app layout). Useful for embedding this
// reporting UI inside other shells (e.g. client area pages).
export const ReportingPageContent: React.FC<ReportingPageContentProps> = ({
  autoApplyOnMount = false,
}) => {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>("profit-loss");
  const [fromMonth, setFromMonth] = useState<string>(getThreeMonthsAgo());
  const [toMonth, setToMonth] = useState<string>(getCurrentMonth());
  const [groupBy, setGroupBy] = useState<"monthwise" | "quarterly">(
    "monthwise",
  );

  // Report generation state
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generationError, setGenerationError] = useState<string>("");
  const [reportId, setReportId] = useState<number | null>(null);
  const [reportName, setReportName] = useState<string>("");

  const autoApplyTriggeredRef = useRef(false);

  const tabs = [
    { id: "profit-loss" as TabType, label: "Profit & Loss" },
    { id: "balance-sheet" as TabType, label: "Balance Sheet" },
    { id: "cash-flow" as TabType, label: "Cash Flow Statement" },
    { id: "ar-aging" as TabType, label: "AR Aging" },
    { id: "ap-aging" as TabType, label: "Ap Aging" },
  ];

  const handleApply = useCallback(async () => {
    setGenerationError("");
    setReportId(null);
    setReportName("");

    // Validate dates
    if (!fromMonth || !toMonth) {
      setGenerationError("Please select both From and To months.");
      return;
    }

    if (fromMonth > toMonth) {
      setGenerationError("From month must be before or equal to To month.");
      return;
    }

    setIsGenerating(true);
    try {
      // Convert month format to date range
      const fromDate = getFirstDayOfMonth(fromMonth);
      const toDate = getLastDayOfMonth(toMonth);

      const params: GenerateReportParams = {
        from_date: fromDate,
        to_date: toDate,
        group_by: groupBy,
        save_report: true,
      };

      const response = await generateReport(params);
      setReportId(response.data.report_id || null);
      setReportName(response.data.report_name || "");
    } catch (err: any) {
      console.error("Failed to generate report:", err);
      setGenerationError(
        err.message || "Failed to generate report. Please try again.",
      );
    } finally {
      setIsGenerating(false);
    }
  }, [fromMonth, toMonth, groupBy]);

  useEffect(() => {
    if (!autoApplyOnMount || autoApplyTriggeredRef.current) {
      return;
    }
    // Avoid auto-applying before auth state is resolved (token not ready yet).
    if (authLoading || !isAuthenticated) {
      return;
    }
    autoApplyTriggeredRef.current = true;
    const timeoutId = window.setTimeout(() => {
      handleApply();
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [autoApplyOnMount, handleApply, authLoading, isAuthenticated]);

  return (
    <div className="flex h-full w-full flex-col items-start">
      {/* Main Content */}
      <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto">
        <div className="flex w-full h-full flex-col items-start">
          {/* Tabs Navigation */}
          <div className="flex w-full flex-col items-start border-b border-solid border-neutral-border bg-default-background px-8">
            <Tabs className="w-full">
              {tabs.map((tab) => (
                <Tabs.Item
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </Tabs.Item>
              ))}
            </Tabs>
          </div>

          {/* Tab Content */}
          <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto">
            {activeTab === "profit-loss" && (
              <div className="flex w-full flex-col items-start">
                {/* Profit & Loss Header */}
                <div className="flex w-full items-center justify-between px-8 py-6 border-b border-solid border-neutral-border">
                  <div className="flex items-center gap-2">
                    <h1 className="text-heading-1 font-heading-1 text-default-font">
                      Profit & Loss
                    </h1>
                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-neutral-100 hover:bg-neutral-200 cursor-pointer">
                      <FeatherInfo className="w-3 h-3 text-subtext-color" />
                    </div>
                  </div>
                  {/* Filter Controls */}
                  <div className="flex items-center gap-3">
                    <span className="text-body font-body text-default-font">
                      From
                    </span>
                    <TextField className="w-40" label="" helpText="">
                      <TextField.Input
                        type="month"
                        value={fromMonth}
                        onChange={(e) => setFromMonth(e.target.value)}
                        disabled={isGenerating}
                      />
                    </TextField>
                    <span className="text-body font-body text-default-font">
                      To
                    </span>
                    <TextField className="w-40" label="" helpText="">
                      <TextField.Input
                        type="month"
                        value={toMonth}
                        onChange={(e) => setToMonth(e.target.value)}
                        disabled={isGenerating}
                      />
                    </TextField>
                    <select
                      value={groupBy}
                      onChange={(e) =>
                        setGroupBy(e.target.value as "monthwise" | "quarterly")
                      }
                      disabled={isGenerating}
                      className="px-3 py-2 border border-neutral-border rounded-md bg-default-background text-body font-body text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="monthwise">Monthwise</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                    <Button
                      variant="brand-primary"
                      onClick={handleApply}
                      disabled={isGenerating}
                      icon={
                        isGenerating ? (
                          <FeatherLoader className="animate-spin" />
                        ) : undefined
                      }
                    >
                      {isGenerating ? "Generating..." : "Apply"}
                    </Button>
                  </div>
                </div>

                {/* Content Area */}
                <div className="flex w-full grow shrink-0 basis-0 flex-col items-start">
                  {generationError && (
                    <div className="w-full px-8 py-4 bg-red-50 border-b border-red-200">
                      <div className="text-body font-body text-red-700">
                        {generationError}
                      </div>
                    </div>
                  )}
                  {isGenerating && (
                    <div className="flex w-full grow shrink-0 basis-0 flex-col items-center justify-center p-8">
                      <div className="flex flex-col items-center gap-4">
                        <FeatherLoader className="w-12 h-12 text-brand-600 animate-spin" />
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-heading-3 font-heading-3 text-default-font">
                            Generating Profit & Loss Report
                          </span>
                          <span className="text-body font-body text-subtext-color">
                            This may take a few moments...
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {!isGenerating && reportId && activeTab === "profit-loss" && (
                    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start">
                      <InlineExcelViewer
                        reportId={reportId}
                        reportName={reportName}
                        targetSheet="IS"
                        fromDate={getFirstDayOfMonth(fromMonth)}
                        toDate={getLastDayOfMonth(toMonth)}
                      />
                    </div>
                  )}
                  {!isGenerating && !reportId && !generationError && (
                    <div className="flex w-full grow shrink-0 basis-0 flex-col items-center justify-center p-8">
                      <div className="text-body font-body text-subtext-color">
                        Select date range and click Apply to generate Profit &
                        Loss report
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "balance-sheet" && (
              <div className="flex w-full flex-col items-start">
                {/* Balance Sheet Header */}
                <div className="flex w-full items-center justify-between px-8 py-6 border-b border-solid border-neutral-border">
                  <div className="flex items-center gap-2">
                    <h1 className="text-heading-1 font-heading-1 text-default-font">
                      Balance Sheet
                    </h1>
                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-neutral-100 hover:bg-neutral-200 cursor-pointer">
                      <FeatherInfo className="w-3 h-3 text-subtext-color" />
                    </div>
                  </div>
                  {/* Filter Controls */}
                  <div className="flex items-center gap-3">
                    <span className="text-body font-body text-default-font">
                      From
                    </span>
                    <TextField className="w-40" label="" helpText="">
                      <TextField.Input
                        type="month"
                        value={fromMonth}
                        onChange={(e) => setFromMonth(e.target.value)}
                        disabled={isGenerating}
                      />
                    </TextField>
                    <span className="text-body font-body text-default-font">
                      To
                    </span>
                    <TextField className="w-40" label="" helpText="">
                      <TextField.Input
                        type="month"
                        value={toMonth}
                        onChange={(e) => setToMonth(e.target.value)}
                        disabled={isGenerating}
                      />
                    </TextField>
                    <select
                      value={groupBy}
                      onChange={(e) =>
                        setGroupBy(e.target.value as "monthwise" | "quarterly")
                      }
                      disabled={isGenerating}
                      className="px-3 py-2 border border-neutral-border rounded-md bg-default-background text-body font-body text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="monthwise">Monthwise</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                    <Button
                      variant="brand-primary"
                      onClick={handleApply}
                      disabled={isGenerating}
                      icon={
                        isGenerating ? (
                          <FeatherLoader className="animate-spin" />
                        ) : undefined
                      }
                    >
                      {isGenerating ? "Generating..." : "Apply"}
                    </Button>
                  </div>
                </div>

                {/* Content Area */}
                <div className="flex w-full grow shrink-0 basis-0 flex-col items-start">
                  {generationError && (
                    <div className="w-full px-8 py-4 bg-red-50 border-b border-red-200">
                      <div className="text-body font-body text-red-700">
                        {generationError}
                      </div>
                    </div>
                  )}
                  {isGenerating && (
                    <div className="flex w-full grow shrink-0 basis-0 flex-col items-center justify-center p-8">
                      <div className="flex flex-col items-center gap-4">
                        <FeatherLoader className="w-12 h-12 text-brand-600 animate-spin" />
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-heading-3 font-heading-3 text-default-font">
                            Generating Balance Sheet Report
                          </span>
                          <span className="text-body font-body text-subtext-color">
                            This may take a few moments...
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {!isGenerating &&
                    reportId &&
                    activeTab === "balance-sheet" && (
                      <div className="flex w-full grow shrink-0 basis-0 flex-col items-start">
                        <InlineExcelViewer
                          reportId={reportId}
                          reportName={reportName}
                          targetSheet="BS"
                          fromDate={getFirstDayOfMonth(fromMonth)}
                          toDate={getLastDayOfMonth(toMonth)}
                        />
                      </div>
                    )}
                  {!isGenerating && !reportId && !generationError && (
                    <div className="flex w-full grow shrink-0 basis-0 flex-col items-center justify-center p-8">
                      <div className="text-body font-body text-subtext-color">
                        Select date range and click Apply to generate Balance
                        Sheet report
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "cash-flow" && (
              <div className="flex w-full flex-col items-start">
                {/* Cash Flow Statement Header */}
                <div className="flex w-full items-center justify-between px-8 py-6 border-b border-solid border-neutral-border">
                  <div className="flex items-center gap-2">
                    <h1 className="text-heading-1 font-heading-1 text-default-font">
                      Cash Flow Statement
                    </h1>
                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-neutral-100 hover:bg-neutral-200 cursor-pointer">
                      <FeatherInfo className="w-3 h-3 text-subtext-color" />
                    </div>
                  </div>
                  {/* Filter Controls */}
                  <div className="flex items-center gap-3">
                    <span className="text-body font-body text-default-font">
                      From
                    </span>
                    <TextField className="w-40" label="" helpText="">
                      <TextField.Input
                        type="month"
                        value={fromMonth}
                        onChange={(e) => setFromMonth(e.target.value)}
                        disabled={isGenerating}
                      />
                    </TextField>
                    <span className="text-body font-body text-default-font">
                      To
                    </span>
                    <TextField className="w-40" label="" helpText="">
                      <TextField.Input
                        type="month"
                        value={toMonth}
                        onChange={(e) => setToMonth(e.target.value)}
                        disabled={isGenerating}
                      />
                    </TextField>
                    <select
                      value={groupBy}
                      onChange={(e) =>
                        setGroupBy(e.target.value as "monthwise" | "quarterly")
                      }
                      disabled={isGenerating}
                      className="px-3 py-2 border border-neutral-border rounded-md bg-default-background text-body font-body text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="monthwise">Monthwise</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                    <Button
                      variant="brand-primary"
                      onClick={handleApply}
                      disabled={isGenerating}
                      icon={
                        isGenerating ? (
                          <FeatherLoader className="animate-spin" />
                        ) : undefined
                      }
                    >
                      {isGenerating ? "Generating..." : "Apply"}
                    </Button>
                  </div>
                </div>

                {/* Content Area */}
                <div className="flex w-full grow shrink-0 basis-0 flex-col items-start">
                  {generationError && (
                    <div className="w-full px-8 py-4 bg-red-50 border-b border-red-200">
                      <div className="text-body font-body text-red-700">
                        {generationError}
                      </div>
                    </div>
                  )}
                  {isGenerating && (
                    <div className="flex w-full grow shrink-0 basis-0 flex-col items-center justify-center p-8">
                      <div className="flex flex-col items-center gap-4">
                        <FeatherLoader className="w-12 h-12 text-brand-600 animate-spin" />
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-heading-3 font-heading-3 text-default-font">
                            Generating Cash Flow Statement
                          </span>
                          <span className="text-body font-body text-subtext-color">
                            This may take a few moments...
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {!isGenerating && reportId && activeTab === "cash-flow" && (
                    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start">
                      <InlineExcelViewer
                        reportId={reportId}
                        reportName={reportName}
                        targetSheet="CFS"
                        fromDate={getFirstDayOfMonth(fromMonth)}
                        toDate={getLastDayOfMonth(toMonth)}
                      />
                    </div>
                  )}
                  {!isGenerating && !reportId && !generationError && (
                    <div className="flex w-full grow shrink-0 basis-0 flex-col items-center justify-center p-8">
                      <div className="text-body font-body text-subtext-color">
                        Select date range and click Apply to generate Cash Flow
                        Statement
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "ar-aging" && (
              <div className="flex w-full flex-col items-start p-8 gap-6">
                <h1 className="text-heading-1 font-heading-1 text-default-font">
                  AR Aging
                </h1>
                {/* Content will be added later */}
              </div>
            )}

            {activeTab === "ap-aging" && (
              <div className="flex w-full flex-col items-start p-8 gap-6">
                <h1 className="text-heading-1 font-heading-1 text-default-font">
                  Ap Aging
                </h1>
                {/* Content will be added later */}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
